import { useEffect, useState } from 'react';
import { useMobileStore, type MobileScreen } from '@/stores/mobile-store';
import { MobileTabBar } from './MobileTabBar';
import { MobileHeader } from './MobileHeader';
import { ConfirmDialogHost } from '@/components/shared/ConfirmDialogHost';
import { ShareDialog } from '@/components/modals/ShareDialog';
import { useModalStore } from '@/stores/modal-store';
import { ConnectionListScreen } from './screens/ConnectionListScreen';
import { SchemaBrowserScreen } from './screens/SchemaBrowserScreen';
import { DataViewScreen } from './screens/DataViewScreen';
import { RecordDetailScreen } from './screens/RecordDetailScreen';
import { QueryListScreen, QueryEditorScreen, QueryRecordDetailScreen } from './screens/QueryScreen';
import { TerminalListScreen, MobileTerminalViewScreen } from './screens/TerminalScreen';
import { LogsScreen } from './screens/LogsScreen';
import { MoreScreen } from './screens/MoreScreen';

/**
 * iOS Safari does not shrink `100vh` or `100dvh` when the on-screen keyboard
 * appears — the keyboard floats over the page and covers UI. Track the visual
 * viewport so the shell sizes itself to the area that's actually visible.
 */
function useVisibleViewportHeight(): string {
  const [height, setHeight] = useState<string>('100dvh');

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => setHeight(`${vv.height}px`);
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return height;
}

export function MobileShell() {
  const activeTab = useMobileStore((s) => s.activeTab);
  const stacks = useMobileStore((s) => s.stacks);
  const popScreen = useMobileStore((s) => s.popScreen);
  const viewportHeight = useVisibleViewportHeight();
  const shareDialog = useModalStore((s) => s.shareDialog);
  const closeShareDialog = useModalStore((s) => s.closeShareDialog);

  const currentStack = stacks[activeTab];
  const currentScreen = currentStack[currentStack.length - 1];
  const canGoBack = currentStack.length > 1;

  return (
    <div
      className="w-screen flex flex-col bg-dbgo-root"
      style={{
        height: viewportHeight,
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      <MobileHeader
        title={currentScreen.title}
        canGoBack={canGoBack}
        onBack={popScreen}
      />

      <div className="flex-1 min-h-0 overflow-hidden">
        <MobileScreenRenderer screen={currentScreen} />
      </div>

      <MobileTabBar />

      {/* Shared destructive-confirm dialog (useDialogStore.showConfirm) */}
      <ConfirmDialogHost />

      {/* Share dialog — same modal as desktop, driven from useModalStore. */}
      {shareDialog.mode === 'open' && (
        <ShareDialog
          resourceType={shareDialog.resourceType}
          resourceId={shareDialog.resourceId}
          resourceName={shareDialog.resourceName}
          onClose={closeShareDialog}
        />
      )}
    </div>
  );
}

function MobileScreenRenderer({ screen }: { screen: MobileScreen }) {
  switch (screen.type) {
    case 'connection-list':
      return <ConnectionListScreen />;
    case 'schema-browser':
      return <SchemaBrowserScreen params={screen.params} />;
    case 'data-view':
      return <DataViewScreen params={screen.params} />;
    case 'record-detail':
      return <RecordDetailScreen params={screen.params} />;
    case 'query-list':
      return <QueryListScreen />;
    case 'query-editor':
      return <QueryEditorScreen params={screen.params} />;
    case 'query-record-detail':
      return <QueryRecordDetailScreen params={screen.params} />;
    case 'terminal-list':
      return <TerminalListScreen />;
    case 'terminal-view':
      return <MobileTerminalViewScreen params={screen.params} />;
    case 'logs':
      return <LogsScreen />;
    case 'more':
      return <MoreScreen />;
    default:
      return <div className="p-4 text-text-muted text-sm">Unknown screen: {screen.type}</div>;
  }
}
